import { useState } from "react";
import { useEffect } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { BlockSeason } from "../../api/types";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { formatDate } from "../../lib/format";
import { useOverlayDismiss } from "../../lib/useOverlayDismiss";

interface State {
  status: "loading" | "ready" | "error";
  seasons: BlockSeason[];
  errorMessage: string | null;
}

const initial: State = { status: "loading", seasons: [], errorMessage: null };

// Block seasons are their own timeline (separate from gumball seasons) with no
// coin carryover, so closing is a plain confirm — no carry-into modal. Draft
// status is labelled "Planning" in the UI.
export function BlocksSeasonsPage() {
  const [state, setState] = useState<State>(initial);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState<BlockSeason | null>(null);
  const createKey = useIdempotencyKey();
  const activateKey = useIdempotencyKey();
  const closeKey = useIdempotencyKey();
  const overlayDismiss = useOverlayDismiss(() => setConfirmClose(null), false);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));
    try {
      const res = await apiClient.listBlockSeasons({ limit: 50 });
      setState({ status: "ready", seasons: res.seasons, errorMessage: null });
    } catch (caught) {
      setState({ status: "error", seasons: [], errorMessage: messageFor(caught) });
    }
  }

  // The server replays a stored answer for a reused key, so a refused create,
  // activate or close needs a new key to be retried; a network error may never
  // have reached the server, so that retry keeps the key.
  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = createName.trim();
    if (name === "" || creating) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await apiClient.createBlockSeason({ name, status: "draft" }, createKey.keyFor(name));
      setCreateName("");
      createKey.reset();
      await refresh();
    } catch (caught) {
      if (caught instanceof ApiError) {
        createKey.reset();
      }
      setCreateError(messageFor(caught));
    } finally {
      setCreating(false);
    }
  }

  async function activate(season: BlockSeason) {
    try {
      await apiClient.activateBlockSeason(season.id, activateKey.keyFor(season.id));
      activateKey.reset();
      await refresh();
    } catch (caught) {
      if (caught instanceof ApiError) {
        activateKey.reset();
      }
      setState((previous) => ({ ...previous, errorMessage: messageFor(caught) }));
    }
  }

  async function close(season: BlockSeason) {
    try {
      await apiClient.closeBlockSeason(season.id, closeKey.keyFor(season.id));
      closeKey.reset();
      setConfirmClose(null);
      await refresh();
    } catch (caught) {
      if (caught instanceof ApiError) {
        closeKey.reset();
      }
      setConfirmClose(null);
      setState((previous) => ({ ...previous, errorMessage: messageFor(caught) }));
    }
  }

  return (
    <div className="seasons-view">
      <section className="card">
        <h2>Create planning season</h2>
        <form onSubmit={handleCreate} className="form-row form-row--inline">
          <label>
            Name
            <input
              type="text"
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              placeholder="H2 2026"
              required
            />
          </label>
          <button type="submit" className="btn btn--primary" disabled={creating || createName.trim() === ""}>
            {creating ? "Creating…" : "Create planning season"}
          </button>
        </form>
        {createError !== null && <p className="form-row__error">{createError}</p>}
      </section>

      <section className="card">
        <h2>Block seasons</h2>
        {state.status === "loading" && <p className="loading">Loading…</p>}
        {/* Also set by a failed activate/close while the list itself is ready. */}
        {state.errorMessage !== null && <p className="form-row__error">{state.errorMessage}</p>}
        {state.status === "ready" && state.seasons.length === 0 && (
          <p className="muted">No block seasons yet — create one above.</p>
        )}
        {state.status === "ready" && state.seasons.length > 0 && (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Starts</th>
                <th>Closed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {state.seasons.map((season) => (
                <tr key={season.id}>
                  <td>{season.name}</td>
                  <td>
                    <StatusBadge status={season.status} />
                  </td>
                  <td>{formatDate(season.starts_at)}</td>
                  <td>{formatDate(season.closed_at)}</td>
                  <td className="row-actions">
                    {season.status === "draft" && (
                      <button type="button" className="btn btn--primary btn--sm" onClick={() => void activate(season)}>
                        Activate
                      </button>
                    )}
                    {season.status === "active" && (
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirmClose(season)}>
                        Close…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {confirmClose !== null && (
        <div className="modal-overlay" role="presentation" {...overlayDismiss}>
          <div className="modal" role="dialog" aria-modal="true">
            <header className="modal__header">
              <h2>Close {confirmClose.name}?</h2>
              <button type="button" className="modal__close" onClick={() => setConfirmClose(null)}>
                ×
              </button>
            </header>
            <div className="modal__body">
              <p>
                Closing this season stops new progress and completions. Block seasons carry no coins,
                so nothing is transferred. This cannot be undone.
              </p>
              <div className="modal__footer">
                <button type="button" className="btn btn--ghost" onClick={() => setConfirmClose(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn--primary" onClick={() => void close(confirmClose)}>
                  Close season
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: BlockSeason["status"] }) {
  const label = status === "draft" ? "planning" : status;
  return <span className={`status-badge status-badge--${status}`}>{label}</span>;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
